/**
 * AdministrationPage — the Staff Members console at
 * /operations/administration/staff-members (and the legacy redirect target
 * at /operations/administration).
 *
 * Three tabs driven by `?tab=people|activity|admins`:
 *   - People   → AdminPeoplePage (staff roster + drift status)
 *   - Activity → AdminActivityPage (CloudWatch audit trail)
 *   - Admins   → AdminsPage (grant / revoke admin roles)
 *
 * Tab pattern follows this repo's ad-hoc convention (role="tablist" +
 * role="tab" + aria-selected + local tabClass), same as SearchDetailPage.
 * The active tab lives in the URL so it is bookmarkable and survives reload.
 *
 * Access gated by <AdminRoute> in routes.tsx (frontend) and require_admin()
 * on every /api/web/v2/admin/* endpoint (backend).
 */
import { useSearchParams } from "react-router-dom";
import { AdminActivityPage } from "./admin/AdminActivityPage";
import { AdminPeoplePage } from "./admin/AdminPeoplePage";
import { AdminsPage } from "./admin/AdminsPage";

type TabId = "people" | "activity" | "admins";

const TABS: { id: TabId; label: string }[] = [
  { id: "people", label: "People" },
  { id: "activity", label: "Activity" },
  { id: "admins", label: "Admins" },
];

export function AdministrationPage() {
  const [searchParams, setSearchParams] = useSearchParams();

  const raw = searchParams.get("tab");
  const activeTab: TabId =
    raw === "activity" || raw === "admins" ? raw : "people";

  function selectTab(id: TabId) {
    const next = new URLSearchParams(searchParams);
    next.set("tab", id);
    setSearchParams(next, { replace: true });
  }

  const tabClass = (id: TabId) =>
    `px-4 py-2 text-body font-medium border-b-2 transition-colors ${
      activeTab === id
        ? "border-accent text-accent"
        : "border-transparent text-text-secondary hover:text-text hover:border-border"
    }`;

  return (
    <div className="min-h-full flex flex-col">
      <div className="flex border-b border-border px-6 pt-4" role="tablist" aria-label="Staff Members">
        {TABS.map(({ id, label }) => (
          <button
            key={id}
            type="button"
            role="tab"
            aria-selected={activeTab === id}
            data-testid={`staff-members-tab-${id}`}
            className={tabClass(id)}
            onClick={() => selectTab(id)}
          >
            {label}
          </button>
        ))}
      </div>

      <div className="flex-1">
        {activeTab === "people" && <AdminPeoplePage />}
        {activeTab === "activity" && <AdminActivityPage />}
        {activeTab === "admins" && <AdminsPage />}
      </div>
    </div>
  );
}
